import React from "react";
import { Typography, Box, Stack } from "@mui/material";
import { Tour } from "@/types/dto/tours/tour";
import Image from "next/image";
import { ColletteButton, ColletteChip } from "@collette-travel/collette-common-controls";
import sanitizeHtml from "@/utils/sanitize-html";

interface TourHighlightsCardProps {
  tour: Tour;
  index?: number;
}

const TourHighlightsCard: React.FC<TourHighlightsCardProps> = ({ tour, index = 0 }) => {
  const { heroImage, mediumDescription, tourUrl, badge, title, days, price } = tour;
  const numericPrice = typeof price === "string" ? price.replace(/[^0-9]/g, "") : "";

  const badgeToShow = Array.isArray(badge) && badge.length > 0 ? badge[0] : badge;
  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: { lg: "row", sm: "column" },
        gap: { xs: "2rem", lg: "4rem" },
        backgroundColor: "transparent",
        width: "100%"
      }}
      data-testid={`tour-highlight-card-${index}`}
      data-tour-name={title}
      data-tour-price={numericPrice}
      data-tour-duration={String(days)}
      // data-tour-activity-level="pending-api"
      // data-tour-style="pending-api"
      data-tour-id={(tour as any).neoId}
      data-carousel-position={String(index + 1)}
    >
      <Box
        flexShrink={0}
        component="a"
        href={tourUrl}
        sx={{
          width: { lg: "60.1rem", md: "100%", xs: "100%" },
          height: { lg: "47.7rem", md: "30rem", xs: "16rem" },
          position: "relative"
        }}
      >
        <Image
          src={heroImage.imageUrl || ""}
          alt={heroImage.altText || ""}
          fill
          style={{
            objectFit: "cover",
            borderRadius: "2rem",
            userSelect: "none"
          }}
          sizes="(max-width: 991px) 100vw, 1720px"
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: { legacyDesktop: "flex-start" },
          justifyContent: { legacyDesktop: "flex-start" },
          gap: { xs: "2rem", lg: "4rem" },
          flex: "1 0 0",
          color: "common.white",
          pt: "2rem"
        }}
      >
        {badgeToShow && (
          <ColletteChip
            label={badgeToShow}
            size="medium"
            // @ts-ignore
            color={`${badgeToShow.length > 0 ? "info" : "transparent"}`}
            variant="filled"
            testId={`collette-chip-test-${index}`}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "1.4rem",
              borderRadius: "0.8rem",
              color: "info.contrastText",
              alignSelf: "flex-start"
            }}
          />
        )}

        <Typography
          variant="h2"
          data-testid={`tour-highlight-title-${index}`}
          component="a"
          href={tourUrl}
          color="common.white"
        >
          {title}
        </Typography>
        <Typography
          color="common.white"
          sx={{
            textOverflow: "ellipsis",
            fontSize: "1.8rem",
            lineHeight: "3rem",
            width: "100%"
          }}
          // Safely allow simple inline markup without rendering <style> tags
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(mediumDescription) }}
        />
        <ColletteButton
          label={`View the ${days}-Day Itinerary`}
          href={tourUrl}
          data-track="click"
          variant="outlined"
          size="large"
          sx={{
            alignSelf: { lg: "flex-start", md: "center", xs: "center" },
            backgroundColor: "background.default"
          }}
          testId={` tour-highlight-cta-button-${index}`}
        />
      </Box>
    </Stack>
  );
};

export default TourHighlightsCard;
